"use client";

import { Box, Grid2 as Grid } from "@mui/material";
import { generateMediaSrc } from "@v2/components/MediaV2";
import Link from "next/link";
import { LandingPageComponent } from "../types";
import LandingPageComponentLoader from "../landingPageComponentLoader";
import { blockCellNodePath, StudioNodePath, studioNodeAttributes, useStudioNodePath } from "@v2/components/studio/context";
import { capWidthBudget, CONTAINER_WIDTH, mediaWidthFor, narrowWidthBudget, useWidthBudget, WidthBudgetProvider } from "./widthBudget";

type CustomStyle = Record<string, string | number>;

type ResponsiveCustomStyle = {
    mobile?: CustomStyle;
    tablet?: CustomStyle;
    desktop?: CustomStyle;
};

export default function BlockLC({ content }: {
    content: {
        data: LandingBlock[],
        settings: {
            gap?: string,
            column_gap?: string,
            row_gap?: string,
            full_width: boolean,
            background_color?: string | null,
            custom_style?: ResponsiveCustomStyle,
            justify_content?: string | null,
            align_items?: string | null,
            mobile?: BlockResponsiveSettings,
            tablet?: BlockResponsiveSettings,
            desktop?: BlockResponsiveSettings,
        }
    };
}) {

    const s = content.settings;
    // This block's own path in the page JSON, or null outside studio mode.
    const studioPath = useStudioNodePath();
    // How wide this block is drawn. A nested block inherits the cell it sits in, so a
    // card three columns deep asks for a card-sized file rather than a page-sized one.
    const parentBudget = useWidthBudget();
    const legacyGap = Number(s.gap ?? 0);
    const colGapPx = Number(s.column_gap ?? legacyGap);
    const rowGapPx = Number(s.row_gap ?? legacyGap);

    const legacyResponsiveGap = (px: number, factor: number, minimum: number) => px === 0
        ? 0
        : Math.min(Math.max(Math.round(px * factor), minimum), px);

    const responsiveSettings = {
        mobile: normalizeResponsiveSettings(s.mobile, {
            column_gap: legacyResponsiveGap(colGapPx, 0.5, 8),
            row_gap: legacyResponsiveGap(rowGapPx, 0.5, 8),
            justify_content: s.justify_content,
            align_items: s.align_items,
        }),
        tablet: normalizeResponsiveSettings(s.tablet, {
            column_gap: legacyResponsiveGap(colGapPx, 0.75, 12),
            row_gap: legacyResponsiveGap(rowGapPx, 0.75, 12),
            justify_content: s.justify_content,
            align_items: s.align_items,
        }),
        desktop: normalizeResponsiveSettings(s.desktop, {
            column_gap: colGapPx,
            row_gap: rowGapPx,
            justify_content: s.justify_content,
            align_items: s.align_items,
        }),
    };

    const containerCustomSx = makeResponsiveCustomSx(s.custom_style);

    const contentBudget = narrowWidthBudget(
        s.full_width ? parentBudget : capWidthBudget(parentBudget, CONTAINER_WIDTH),
        { xs: 12, sm2: 12, lg: 12 },
        { xs: responsiveSettings.mobile.px, sm2: responsiveSettings.tablet.px, lg: responsiveSettings.desktop.px },
    );

    return <Box sx={{
        backgroundColor: s.background_color || undefined,
        display: {
            xs: responsiveSettings.mobile.visible ? 'block' : 'none',
            sm2: responsiveSettings.tablet.visible ? 'block' : 'none',
            lg: responsiveSettings.desktop.visible ? 'block' : 'none',
        },
        px: {
            xs: `${responsiveSettings.mobile.px}px`,
            sm2: `${responsiveSettings.tablet.px}px`,
            lg: `${responsiveSettings.desktop.px}px`,
        },
        py: {
            xs: `${responsiveSettings.mobile.py}px`,
            sm2: `${responsiveSettings.tablet.py}px`,
            lg: `${responsiveSettings.desktop.py}px`,
        },
        ...containerCustomSx,
    }}>
        <Grid container
            columnSpacing={{
                xs: `${responsiveSettings.mobile.column_gap}px`,
                sm2: `${responsiveSettings.tablet.column_gap}px`,
                lg: `${responsiveSettings.desktop.column_gap}px`,
            }}
            rowSpacing={{
                xs: `${responsiveSettings.mobile.row_gap}px`,
                sm2: `${responsiveSettings.tablet.row_gap}px`,
                lg: `${responsiveSettings.desktop.row_gap}px`,
            }}
            sx={{
            maxWidth: s.full_width ? 'none' : 1400,
            margin: 'auto',
            px: s.full_width ? 0 : { xs: 2, lg2: 0 },
            flexDirection: {
                xs: responsiveSettings.mobile.flex_direction,
                sm2: responsiveSettings.tablet.flex_direction,
                lg: responsiveSettings.desktop.flex_direction,
            },
            justifyContent: {
                xs: responsiveSettings.mobile.justify_content,
                sm2: responsiveSettings.tablet.justify_content,
                lg: responsiveSettings.desktop.justify_content,
            },
            alignItems: {
                xs: responsiveSettings.mobile.align_items,
                sm2: responsiveSettings.tablet.align_items,
                lg: responsiveSettings.desktop.align_items,
            },
        }}>
            {content.data.map((block, index) => {
                if (!block.show_in_site) return null;

                const size = {
                    xs: block.basic.mobile_grid.visible ? Number(block.basic.mobile_grid.col) : 0,
                    sm2: block.basic.tablet_grid.visible ? Number(block.basic.tablet_grid.col) : 0,
                    lg: block.basic.desktop_grid.visible ? Number(block.basic.desktop_grid.col) : 0,
                };

                const order = {
                    xs: block.basic.mobile_grid.visible ? Number(block.basic.mobile_grid.order) : undefined,
                    sm2: block.basic.tablet_grid.visible ? Number(block.basic.tablet_grid.order) : undefined,
                    lg: block.basic.desktop_grid.visible ? Number(block.basic.desktop_grid.order) : undefined,
                };

                const display = {
                    xs: block.basic.mobile_grid.visible ? "block" : "none",
                    sm2: block.basic.tablet_grid.visible ? "block" : "none",
                    lg: block.basic.desktop_grid.visible ? "block" : "none",
                };

                const cellBudget = narrowWidthBudget(contentBudget, size, {
                    xs: Number(block.basic.mobile_grid.px ?? 0),
                    sm2: Number(block.basic.tablet_grid.px ?? 0),
                    lg: Number(block.basic.desktop_grid.px ?? 0),
                });

                const bg = block.background;
                const mobileUrl = bg.mobile_media_id ?? bg.tablet_media_id ?? bg.desktop_media_id;
                const tabletUrl = bg.tablet_media_id ?? bg.desktop_media_id ?? bg.mobile_media_id;
                const desktopUrl = bg.desktop_media_id ?? bg.tablet_media_id ?? bg.mobile_media_id;

                const overlayValue = Number(block.background.overlay ?? 0) / 100;
                const overlayDir = block.background.overlay_direction;
                const overlayGradient = overlayValue > 0
                    ? (!overlayDir
                        ? `linear-gradient(rgba(0,0,0,${overlayValue}),rgba(0,0,0,${overlayValue}))`
                        : `linear-gradient(${overlayDir}, rgba(0,0,0,${overlayValue}), rgba(0,0,0,0))`)
                    : null;

                const makeBgImage = (id: string | null, bp: 'xs' | 'sm2' | 'lg') => {
                    const url = id ? generateMediaSrc(id, { action: 'thumbnail', width: mediaWidthFor(cellBudget[bp]) }) : null;
                    if (url) return [overlayGradient, `url(${url})`].filter(Boolean).join(',');
                    return overlayGradient ?? "none";
                };

                const ratio = {
                    xs: block.basic.mobile_grid.ratio,
                    sm2: block.basic.tablet_grid.ratio,
                    lg: block.basic.desktop_grid.ratio,
                };

                const paddingX = {
                    xs: `${block.basic.mobile_grid.px ?? 0}px`,
                    sm2: `${block.basic.tablet_grid.px ?? 0}px`,
                    lg: `${block.basic.desktop_grid.px ?? 0}px`,
                };

                const paddingY = {
                    xs: `${block.basic.mobile_grid.py ?? 0}px`,
                    sm2: `${block.basic.tablet_grid.py ?? 0}px`,
                    lg: `${block.basic.desktop_grid.py ?? 0}px`,
                };

                const border = block.border;
                const borderWidth = border?.width ? parseInt(border.width) : 0;
                const borderColor = border?.color ?? null;
                const borderRadius = border?.radius ? parseInt(border.radius) : 0;
                const hasBorder = borderWidth > 0 && !!borderColor;
                const hoverSx = hoverEffectSx[block.basic.hover_effect ?? ''] ?? {};
                const responsiveLayout = normalizeSubBlockResponsiveLayout(block.basic);
                const customSx = makeResponsiveCustomSx(block.custom_style);
                const cellPath = blockCellNodePath(studioPath, index);

                return <Grid size={size}
                    order={order} key={`${block.id}-${index}`}
                    {...(block.basic.link && {
                        component: Link,
                        href: block.basic.link ?? ''
                    })} sx={{ display }}
                    {...studioNodeAttributes(cellPath, 'cell', block.id)}>
                    <Box
                        sx={{
                            width: "100%",
                            aspectRatio: {
                                xs: ratio.xs === '100%' ? undefined : ratio.xs,
                                sm2: ratio.sm2 === '100%' ? undefined : ratio.sm2,
                                lg: ratio.lg === '100%' ? undefined : ratio.lg,
                            },
                            minHeight: {
                                xs: ratio.xs === '100%' ? '100%' : undefined,
                                sm2: ratio.sm2 === '100%' ? '100%' : undefined,
                                lg: ratio.lg === '100%' ? '100%' : undefined,
                            },
                            position: "relative",
                            overflow: "hidden",
                            backgroundColor: block.background.color ?? "transparent",
                            backgroundImage: {
                                xs: makeBgImage(mobileUrl, 'xs'),
                                sm2: makeBgImage(tabletUrl, 'sm2'),
                                lg: makeBgImage(desktopUrl, 'lg'),
                            },
                            backgroundSize: block.background.size,
                            backgroundPosition: block.background.position,
                            backgroundRepeat: "no-repeat",
                            display: 'flex',
                            flexDirection: {
                                xs: responsiveLayout.mobile.flex_direction,
                                sm2: responsiveLayout.tablet.flex_direction,
                                lg: responsiveLayout.desktop.flex_direction,
                            },
                            alignItems: {
                                xs: responsiveLayout.mobile.align_items,
                                sm2: responsiveLayout.tablet.align_items,
                                lg: responsiveLayout.desktop.align_items,
                            },
                            justifyContent: {
                                xs: responsiveLayout.mobile.justify_content,
                                sm2: responsiveLayout.tablet.justify_content,
                                lg: responsiveLayout.desktop.justify_content,
                            },
                            px: paddingX,
                            py: paddingY,
                            ...(hasBorder && {
                                border: `${borderWidth}px solid ${borderColor}`,
                            }),
                            borderRadius: `${borderRadius ?? 0}px`,
                            ...hoverSx,
                            ...customSx,
                        }}
                    >
                        {block.components?.length > 0 && (
                            <StudioNodePath path={cellPath}>
                                <WidthBudgetProvider value={cellBudget}>
                                    <LandingPageComponentLoader
                                        components={block.components}
                                        nested
                                        disableLinks={!!block.basic.link}
                                        containerSx={{ display: 'contents' }}
                                    />
                                </WidthBudgetProvider>
                            </StudioNodePath>
                        )}
                    </Box>
                </Grid>
            })}
        </Grid>
    </Box>
}

type LandingBlock = {
    id: number;
    basic: BlockBasic;
    border: BlockBorder;
    components: LandingPageComponent[],
    background: BlockBackground;
    show_in_site: boolean;
    custom_style?: ResponsiveCustomStyle;
};

type BlockBasic = {
    link: string | null;
    mobile_grid: BlockGrid;
    tablet_grid: BlockGrid;
    desktop_grid: BlockGrid;
    responsive_layout?: {
        mobile?: SubBlockResponsiveLayout;
        tablet?: SubBlockResponsiveLayout;
        desktop?: SubBlockResponsiveLayout;
    };
    align_items?: string | null;
    justify_content?: string | null;
    hover_effect?: string | null;
};

type SubBlockResponsiveLayout = {
    flex_direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    align_items?: string | null;
    justify_content?: string | null;
};

function normalizeSubBlockResponsiveLayout(basic: BlockBasic) {
    const normalizeViewport = (viewport: 'mobile' | 'tablet' | 'desktop') => ({
        flex_direction: basic.responsive_layout?.[viewport]?.flex_direction ?? 'column',
        align_items: basic.responsive_layout?.[viewport]?.align_items ?? basic.align_items ?? 'stretch',
        justify_content: basic.responsive_layout?.[viewport]?.justify_content ?? basic.justify_content ?? 'flex-start',
    });

    return {
        mobile: normalizeViewport('mobile'),
        tablet: normalizeViewport('tablet'),
        desktop: normalizeViewport('desktop'),
    };
}

type BlockGrid = {
    col: string;
    order: string;
    ratio: string;
    px: string;
    py: string;
    visible: boolean;
};

type BlockResponsiveSettings = {
    visible?: boolean;
    column_gap?: number | string;
    row_gap?: number | string;
    px?: number | string;
    py?: number | string;
    flex_direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    justify_content?: string | null;
    align_items?: string | null;
};

function normalizeResponsiveSettings(settings: BlockResponsiveSettings | undefined, fallback: BlockResponsiveSettings) {
    return {
        visible: settings?.visible ?? true,
        column_gap: Number(settings?.column_gap ?? fallback.column_gap ?? 0),
        row_gap: Number(settings?.row_gap ?? fallback.row_gap ?? 0),
        px: Number(settings?.px ?? 0),
        py: Number(settings?.py ?? 0),
        flex_direction: settings?.flex_direction ?? 'row',
        justify_content: settings?.justify_content ?? fallback.justify_content ?? 'flex-start',
        align_items: settings?.align_items ?? fallback.align_items ?? 'stretch',
    };
}

function makeResponsiveCustomSx(style?: ResponsiveCustomStyle) {
    if (!style) return {};

    const properties = new Set([
        ...Object.keys(style.mobile ?? {}),
        ...Object.keys(style.tablet ?? {}),
        ...Object.keys(style.desktop ?? {}),
    ]);

    return Object.fromEntries([...properties]
        .filter(property => /^[a-zA-Z][a-zA-Z0-9]*$/.test(property))
        .map(property => {
            const mobileValue = style.mobile?.[property];
            const tabletValue = style.tablet?.[property] ?? mobileValue;
            const desktopValue = style.desktop?.[property] ?? tabletValue;

            return [property, { xs: mobileValue, sm2: tabletValue, lg: desktopValue }];
        })
        .filter(([, value]) => Object.values(value).some(item => typeof item === 'string' || typeof item === 'number')));
}

type BlockBorder = {
    width: string;
    color: string | null;
    radius: string;
} | null;

const hoverEffectSx: Record<string, Record<string, any>> = {
    lift: { transition: 'transform 0.25s, box-shadow 0.25s', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } },
    shadow: { transition: 'box-shadow 0.25s', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } },
    scale: { transition: 'transform 0.25s', '&:hover': { transform: 'scale(1.02)' } },
};

type BlockBackground = {
    size: "cover" | "contain";
    color: string | null;
    overlay: string;
    overlay_direction: string | null;
    position: "center" | "top" | "bottom" | "left" | "right";
    mobile_media_id: string | null;
    tablet_media_id: string | null;
    desktop_media_id: string | null;
};
